import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import defaultImage from './default-image.png';
import { trackPromoCodeCopy, trackShopButtonClick } from '../../utils/analytics';
import { transformAmazonImageUrl, IMAGE_SIZE } from '../../utils/imageUrlTransformer';
import { getDealPricing } from '../../utils/dealPricing';
import PromoCodeModal from '../common/PromoCodeModal';

const Card = ({
  _id,
  url,
  title,
  image_url,
  orig_price,
  retail_price,
  promoPrice,
  promo_price,
  disc_price,
  discount_price,
  promoCode,
  promo_code,
  percOff,
  percent_discount,
  tags,
  sales_rank_in_browsenode,
  imageOnly = false,
  // The product page's hero image is its Largest Contentful Paint: load it right away
  priority = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  const id = _id || `deal_${Math.random().toString(36).substr(2, 9)}`;

  const pricing = useMemo(
    () => getDealPricing({ orig_price, retail_price, promoPrice, promo_price, disc_price, discount_price, percOff, percent_discount }),
    [orig_price, retail_price, promoPrice, promo_price, disc_price, discount_price, percOff, percent_discount],
  );
  const percentOff = pricing.percentOff;
  const formattedSalePrice = pricing.salePrice !== null ? pricing.salePrice.toFixed(2) : '';
  // Only strike through an original price that is actually higher than the sale price
  const formattedOriginalPrice = pricing.hasDiscount ? pricing.originalPrice.toFixed(2) : '';
  const hasPromoCode = !!(promoCode || promo_code);

  const tagsArray = Array.isArray(tags) ? tags : [];
  const isLowestPrice = tagsArray.includes('lowest_price') || tagsArray.includes('lowest_price_ever');
  const isBestSeller = tagsArray.includes('best_seller') || (sales_rank_in_browsenode && sales_rank_in_browsenode <= 3);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleCodeCopy = useCallback(async () => {
    const code = promoCode || promo_code;
    try {
      await navigator.clipboard.writeText(code);
      setShowModal(true);

      const dealData = {
        _id,
        title,
        promo_code: promo_code || promoCode,
      };
      trackPromoCodeCopy(dealData);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('Copy');
      textArea.remove();
      setShowModal(true);

      const dealData = {
        _id,
        title,
        promo_code: promo_code || promoCode,
      };
      trackPromoCodeCopy(dealData);
    }
  }, [promoCode, promo_code, _id, title]);

  const handleShopClick = useCallback(() => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [url]);

  const handleCardClick = useCallback(() => {
    if (hasPromoCode) {
      handleCodeCopy();
    } else {
      handleShopClick();
    }
  }, [hasPromoCode, handleCodeCopy, handleShopClick]);

  const handlePromoCopyClick = useCallback((e) => {
    e.stopPropagation();
    handleCodeCopy();
  }, [handleCodeCopy]);

  // Transform Amazon image URL to use appropriate size for grid view
  const imageUrl = imageError ? defaultImage : (image_url ? transformAmazonImageUrl(image_url, IMAGE_SIZE.MEDIUM_GRID) : defaultImage);

  return (
    <>
      <article
        className="deal-tile"
        role="article"
        aria-labelledby={`deal-title-${id}`}
        onClick={handleCardClick}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
        aria-label={`Shop deal: ${title}`}
      >
        {/* Image container */}
        <div className="deal-tile__image-wrap">
          <img
            className="deal-tile__image"
            src={imageUrl}
            alt={title || 'Product Image'}
            onError={handleImageError}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
          />
          {/* Badges - right side */}
          {percentOff > 0 && (
            <span className="deal-tile__badge deal-tile__badge--off" aria-label={`${percentOff}% discount`}>
              {percentOff}% OFF
            </span>
          )}
          {hasPromoCode && (
            <span className="deal-tile__badge deal-tile__badge--promo">
              PROMO CODE
            </span>
          )}
          {/* Tag icons - bottom right */}
          {(isLowestPrice || isBestSeller) && (
            <div className="deal-tile__tag-icons">
              {isLowestPrice && (
                <span className="deal-tile__tag-icon deal-tile__tag-icon--lowest" aria-label="Lowest price" title="Lowest Price">
                  🏷️
                </span>
              )}
              {isBestSeller && (
                <span className="deal-tile__tag-icon deal-tile__tag-icon--bestseller" aria-label="Best seller" title="Best Seller">
                  🏆
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {!imageOnly && (
          <div className="deal-tile__content">
            <div className="deal-tile__price-row">
              {formattedSalePrice && (
                <span
                  className="deal-tile__sale-price"
                  aria-label={`Sale price $${formattedSalePrice}`}
                >
                  ${formattedSalePrice}
                </span>
              )}
              {formattedOriginalPrice && (
                <span
                  className="deal-tile__orig-price"
                  aria-label={`Original price $${formattedOriginalPrice}`}
                >
                  <del>${formattedOriginalPrice}</del>
                </span>
              )}
            </div>

            <h3
              id={`deal-title-${id}`}
              className="deal-tile__title"
              title={title || 'No title'}
            >
              {title || 'No title available'}
            </h3>

            {hasPromoCode && (
              <button
                className="deal-tile__copy-code"
                onClick={handlePromoCopyClick}
                aria-label={`Copy promo code ${promoCode || promo_code}`}
              >
                <span className="deal-tile__code-text">{promoCode || promo_code}</span>
                <span className="deal-tile__copy-icon">📋 Copy</span>
              </button>
            )}
          </div>
        )}
      </article>

      {showModal && (
        <PromoCodeModal
          code={promoCode || promo_code}
          onClose={() => setShowModal(false)}
          onShop={() => {
            setShowModal(false);
            trackShopButtonClick({
              _id,
              title,
              percent_discount: percent_discount || percOff,
              promo_code: promo_code || promoCode,
              promo_price: promo_price || promoPrice,
            }, 0);
            handleShopClick();
          }}
        />
      )}
    </>
  );
};

Card.propTypes = {
  _id: PropTypes.string,
  url: PropTypes.string,
  title: PropTypes.string,
  image_url: PropTypes.string,
  orig_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  retail_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  promoPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  promo_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disc_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discount_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  promoCode: PropTypes.string,
  promo_code: PropTypes.string,
  percOff: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  percent_discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tags: PropTypes.arrayOf(PropTypes.string),
  sales_rank_in_browsenode: PropTypes.number,
  imageOnly: PropTypes.bool,
  priority: PropTypes.bool,
};

export default Card;
